'use client';

import { createContext, startTransition, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormState, onFormSubmitAction } from './onFormSubmitAction';
import { FormType } from './types';
import { useFormEngine } from './useFormEngine';
import { applyServerIssues } from './utils/applyServerIssues';
import { FormManifest, initialManifest } from './utils/makeManifest';
import { useResettableActionState } from './utils/useResettableActionState';
import { sendGTMEvent } from '@next/third-parties/google';
export type FormBuilderProps = {
  formDefinition: FormType;
  onSuccess?: () => void;
  children: React.ReactNode;
  onError?: () => void;
  headingOverride?: string;
  subheadingOverride?: string;
  ctaOverride?: FormType['callToAction'];
  successHeadingOverride?: string;
  successMessageOverride?: string;
  meetingLinkOverride?: string;
  serviceInterest?: string;
};
export const FormManifestContext = createContext<FormManifest>(initialManifest);
export const FormActionStateContext = createContext<{
  actionState: FormState;
  isPending: boolean;
  resetActionState: () => void;
  onSubmit: () => void;
}>({
  actionState: {
    message: '',
    result: 'new'
  },
  isPending: false,
  resetActionState: () => {},
  onSubmit: () => {}
});
export function FormBuilder({
  formDefinition,
  children,
  onSuccess,
  onError,
  serviceInterest
}: FormBuilderProps) {
  const [actionState, dispatch, isPending, resetActionState] = useResettableActionState(onFormSubmitAction, {
    message: '',
    result: 'new'
  });
  const {
    form,
    manifest
  } = useFormEngine(formDefinition);
  const {
    reset: resetForm,
    setError,
    setFocus
  } = form;
  const onSubmit = form.handleSubmit(async values => {
    if (!formDefinition.name) {
      throw new Error('Form name is required');
    }
    startTransition(() => {
      if (!formDefinition?.name) return;
      const {
        origin,
        pathname
      } = window.location;
      dispatch({
        _conversionFormSlug: formDefinition.id,
        _pageUri: `${origin}${pathname}`,
        _pageName: document.title,
        _serviceInterest: serviceInterest,
        values
      });
    });
  });
  useEffect(() => {
    if (actionState.result === 'success') {
      sendGTMEvent({
        event: 'conversion_form_submitted',
        value: formDefinition.conversionType,
        serviceInterest: serviceInterest,
        location: 'dialog',
        conversionType: formDefinition.conversionType
      });
      resetForm();
      onSuccess?.();
    } else if (actionState.result === 'error') {
      onError?.();
    }
  }, [actionState.result, resetForm, resetActionState, onSuccess, onError, formDefinition.conversionType]);
  useEffect(() => {
    if (actionState.result !== 'error' || !actionState.issues) return;
    applyServerIssues(actionState.issues, setError);
    const first = actionState.issues[0]?.path?.[0] as string | undefined;
    if (actionState.fieldValues) {
      resetForm(actionState.fieldValues, {
        keepErrors: true
      });
    }
    if (first) {
      setFocus(first);
      requestAnimationFrame(() => {
        document.querySelector(`[name="${first}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      });
    }
  }, [actionState.result, actionState.issues, actionState.fieldValues, setError, setFocus, resetForm]);
  return <FormActionStateContext.Provider value={{
    actionState,
    isPending,
    resetActionState,
    onSubmit
  }}>
      <FormManifestContext.Provider value={manifest}>
        <FormProvider {...form}>{children}</FormProvider>
      </FormManifestContext.Provider>
    </FormActionStateContext.Provider>;
}